<template>
  <div class="instance-card" :class="{ 'active': instance.status === 'open' }">
    <div class="instance-header">
      <div class="instance-name">
        <h3>{{ instance.instance_name }}</h3>
        <Tag :value="getStatusLabel(instance.status)" :severity="getStatusSeverity(instance.status)" />
      </div>
      <div class="instance-actions">
        <Button v-if="instance.status === 'open'" 
                icon="pi pi-sign-out" 
                class="p-button-rounded p-button-text p-button-danger" 
                @click="logoutInstance" 
                tooltip="Desconectar"/>
        <Button v-else-if="instance.status === 'connecting' || instance.status === 'created'"
                icon="pi pi-mobile" 
                class="p-button-rounded p-button-text p-button-success" 
                @click="connectInstance" 
                tooltip="Conectar"/>
        <Button icon="pi pi-cog" 
                class="p-button-rounded p-button-text" 
                @click="openSettings" 
                tooltip="Configurações"/>
        <Button icon="pi pi-trash" 
                class="p-button-rounded p-button-text p-button-danger" 
                @click="confirmDelete" 
                tooltip="Excluir"/>
      </div>
    </div>
    
    <div class="instance-content">
      <div v-if="qrcodeVisible" class="qrcode-container">
        <img v-if="qrcodeImage" :src="qrcodeImage" class="qrcode" alt="QR Code" />
        <div v-else class="loading-qrcode">
          <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
          <p>Aguardando QR Code...</p>
        </div>
        <small>Escaneie o QR Code com seu WhatsApp</small>
      </div>
      
      <div v-else class="instance-info">
        <div class="info-item">
          <span class="info-label">Número:</span>
          <span class="info-value">{{ instance.number || 'Não definido' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Webhook:</span>
          <span class="info-value">{{ instance.webhook_url || 'Não configurado' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Criado em:</span>
          <span class="info-value">{{ formatDate(instance.created_at) }}</span>
        </div>
      </div>
    </div>
    
    <div class="instance-footer">
      <Button v-if="instance.status === 'open'" 
              label="Mensagens" 
              icon="pi pi-comments" 
              class="p-button-outlined" 
              @click="goToMessages" />
      <Button v-else-if="!qrcodeVisible" 
              label="Conectar" 
              icon="pi pi-link" 
              class="p-button-success" 
              @click="connectInstance" />
      <Button v-else 
              label="Ocultar QR Code" 
              icon="pi pi-eye-slash" 
              class="p-button-secondary" 
              @click="hideQrCode" />
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';

export default {
  name: 'InstanceCard',
  props: {
    instance: {
      type: Object,
      required: true
    },
    qrcode: {
      type: String,
      default: null
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const router = useRouter();
    const toast = useToast();
    const confirm = useConfirm();
    
    const qrcodeVisible = ref(false);
    const qrcodeImage = computed(() => props.qrcode ? `data:image/png;base64,${props.qrcode}` : null);
    
    const statusLabels = {
      'created': 'Criado',
      'connecting': 'Conectando',
      'open': 'Conectado',
      'disconnected': 'Desconectado',
      'close': 'Fechado'
    };
    
    const statusSeverity = {
      'created': 'info',
      'connecting': 'warning',
      'open': 'success',
      'disconnected': 'danger',
      'close': 'danger'
    };
    
    const getStatusLabel = (status) => {
      return statusLabels[status] || status;
    };
    
    const getStatusSeverity = (status) => {
      return statusSeverity[status] || 'info';
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR');
    };
    
    const connectInstance = async () => {
      try {
        qrcodeVisible.value = true;
        emit('connect', props.instance.id);
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível conectar a instância',
          life: 3000
        });
      }
    };
    
    const hideQrCode = () => {
      qrcodeVisible.value = false;
    };
    
    const logoutInstance = async () => {
      confirm.require({
        message: 'Tem certeza que deseja desconectar esta instância?',
        header: 'Confirmação de Desconexão',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            emit('logout', props.instance.id);
            toast.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Instância desconectada com sucesso',
              life: 3000
            });
          } catch (error) {
            toast.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Não foi possível desconectar a instância',
              life: 3000
            });
          }
        }
      });
    };
    
    const confirmDelete = () => {
      confirm.require({
        message: 'Tem certeza que deseja excluir esta instância?',
        header: 'Confirmação de Exclusão',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: () => {
          emit('delete', props.instance.id);
        }
      });
    };
    
    const openSettings = () => {
      emit('settings', props.instance.id);
    };
    
    const goToMessages = () => {
      router.push(`/whatsapp/instance/${props.instance.id}/messages`);
    };
    
    return {
      qrcodeVisible,
      qrcodeImage,
      getStatusLabel,
      getStatusSeverity,
      formatDate,
      connectInstance,
      logoutInstance,
      confirmDelete,
      openSettings,
      goToMessages,
      hideQrCode
    };
  }
}
</script>

<style scoped>
.instance-card {
  background-color: var(--paper);
  border-radius: 0;
  padding: 1.5rem;
  border: 1px solid var(--rule);
  margin-bottom: 1.5rem;
}

.instance-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.instance-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.instance-name h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.instance-actions {
  display: flex;
  gap: 0.3rem;
}

.instance-content {
  padding: 1rem 0;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
}

.qrcode {
  max-width: 200px;
  margin-bottom: 0.5rem;
}

.loading-qrcode {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.instance-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.info-item {
  display: flex;
  gap: 0.5rem;
}

.info-label {
  font-weight: 600;
  color: var(--ink-soft);
  min-width: 100px;
}

.info-value {
  color: var(--ink);
  word-break: break-word;
}

.instance-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--rule);
}
</style> 